import { ofType } from 'redux-observable'
import { merge, of } from 'rxjs'
import { catchError, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'
import { extractErrorMessage } from 'utils/ErrorMessages'

import { selectActualDefaultLanguage, selectPartner } from 'containers/App/selectors'
import { SCENARIO_TYPE_VALUES } from 'containers/AwarenessCenter/constants'
import { SCENARIOS_REQUEST_ASYNC } from 'containers/AwarenessCenter/Scenarios/constants'
import { addToast } from 'containers/Toasts/actions'

import {
  addScenarioError,
  addScenarioSuccess,
  createCampaignFromIncidentsError,
  createCampaignFromIncidentsSuccess,
  createCampaignFromScenarioSuccess,
  deleteScenarioError,
  deleteScenarioSuccess,
  getAwarenessDetailsError,
  getAwarenessDetailsSuccess,
  getScenarioError,
  getScenarioSuccess,
  reportToTemplateError,
  reportToTemplateSuccess,
  saveScenarioError,
  saveScenarioSuccess,
} from './actions'
import {
  ADD_SCENARIO_ASYNC,
  CREATE_CAMPAIGN_FROM_INCIDENTS_ASYNC,
  CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC,
  DELETE_SCENARIO_ASYNC,
  FETCH_SCENARIOS_ASYNC,
  GET_AWARENESS_DETAILS_ASYNC,
  GET_SCENARIO_ASYNC,
  parseFromEmail,
  REPORT_TO_TEMPLATE_ASYNC,
  SAVE_SCENARIO_ASYNC,
} from './constants'
import { DEFAULT_SCENARIO } from './reducer'
import { selectScenario, selectScenarioLangValues } from './selectors'

export const getScenarioBody = (scenario, scenarioLangValues) => {
  const isAttachment = scenario.type === SCENARIO_TYPE_VALUES.ATTACHMENT
  const scenarioBody = {
    ...scenario,
    scenario_group_name: scenario.scenario_group,
    filetype: isAttachment ? scenario.filetype : null,
    contents: scenario.contents
      .filter(({ locale }) => !scenarioLangValues || scenarioLangValues.includes(locale))
      .map((content) => {
        const values = { ...content }
        if (isAttachment) delete values.filetype
        return values
      }),
  }

  scenarioBody.contents.forEach(({ from_email }, index) => {
    if (from_email[0] === ' ') {
      scenarioBody.contents[index].from_email = from_email.substring(1)
    }

    if (scenarioBody.contents[index].from_email.includes(' ')) {
      const { displayName, from, domain } = parseFromEmail(scenarioBody.contents[index].from_email)

      scenarioBody.contents[index].from_email = `${displayName} <${from}@${domain}>`
    }
  })

  if (scenarioBody.type !== SCENARIO_TYPE_VALUES.ATTACHMENT) {
    scenarioBody.filename = DEFAULT_SCENARIO.filename
  }

  return scenarioBody
}

const fetchScenariosEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_SCENARIOS_ASYNC.INITIATE),
    switchMap(() => {
      const scenarios = state$.value.awarenessCenter?.scenarios

      return api.awarenessCenter
        .getScenarios(
          scenarios?.page || 1,
          scenarios?.sortProperty,
          scenarios?.categoryFilter?.map((elem) => elem.label).join(',') || 0,
          scenarios?.languagesFilter?.map((elem) => elem.value).join(','),
          scenarios?.typeFilter?.map((elem) => elem.value).join(','),
          scenarios?.levelFilter?.map((elem) => elem.value).join(','),
          scenarios?.createdByFilter?.map((elem) => elem.value).join(','),
          scenarios?.search,
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          // the AwarenessCenter list reducer is the only consumer of the result, and its
          // epics are not running when the form is opened from anywhere but that route
          mergeMap((response) =>
            of(
              FETCH_SCENARIOS_ASYNC.onSuccess(response),
              SCENARIOS_REQUEST_ASYNC.onSuccess({ data: response })
            )
          ),
          catchError((error) => of(epicsErrorHandler(FETCH_SCENARIOS_ASYNC.onError, error))),
          takeUntil(action$.pipe(ofType(FETCH_SCENARIOS_ASYNC.CANCEL)))
        )
    })
  )

const getScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_SCENARIO_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .getScenario(
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner,
          action.payload.id
        )
        .pipe(
          map((response) =>
            getScenarioSuccess(response, selectActualDefaultLanguage(state$.value))
          ),
          catchError((error) => of(epicsErrorHandler(getScenarioError, error, [400]))),
          takeUntil(action$.pipe(ofType(GET_SCENARIO_ASYNC.CANCEL)))
        )
    )
  )

export const addScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ADD_SCENARIO_ASYNC.INITIATE),
    mergeMap((action) => {
      const state = state$.value
      const scenario = selectScenario(state)
      const body = getScenarioBody(scenario, selectScenarioLangValues(state))
      return api.awarenessCenter
        .addScenario(body, action.payload.companyId, !!selectPartner(state))
        .pipe(
          map(({ response }) => addScenarioSuccess(response)),
          catchError((error) => of(epicsErrorHandler(addScenarioError, error, [400]))),
          takeUntil(action$.pipe(ofType(ADD_SCENARIO_ASYNC.CANCEL)))
        )
    })
  )

const saveScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_SCENARIO_ASYNC.INITIATE),
    mergeMap((action) => {
      const scenarioBody = getScenarioBody(
        state$.value.scenarioForm.scenario,
        state$.value.scenarioForm.scenarioLangValues
      )

      return api.awarenessCenter
        .saveScenario(
          action.payload.id,
          scenarioBody,
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          map(() => saveScenarioSuccess(scenarioBody)),
          catchError((error) => of(epicsErrorHandler(saveScenarioError, error, [400]))),
          takeUntil(action$.pipe(ofType(SAVE_SCENARIO_ASYNC.CANCEL)))
        )
    })
  )

const deleteScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_SCENARIO_ASYNC.INITIATE),
    mergeMap(() =>
      api.awarenessCenter
        .deleteScenario(
          state$.value.scenarioForm.id,
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          map((response) => deleteScenarioSuccess(response)),
          catchError((error) => of(epicsErrorHandler(deleteScenarioError, error, [400]))),
          takeUntil(action$.pipe(ofType(DELETE_SCENARIO_ASYNC.CANCEL)))
        )
    )
  )

const getAwarenessDetailsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_AWARENESS_DETAILS_ASYNC.INITIATE),
    mergeMap(() =>
      api.awarenessCenter
        .details(
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          map((response) => getAwarenessDetailsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(getAwarenessDetailsError, error, [400]))),
          takeUntil(action$.pipe(ofType(GET_AWARENESS_DETAILS_ASYNC.CANCEL)))
        )
    )
  )

const reportToTemplateEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REPORT_TO_TEMPLATE_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter.reportToTemplate(action.payload.reportId).pipe(
        map((response) => reportToTemplateSuccess(response)),
        catchError((error) => of(epicsErrorHandler(reportToTemplateError, error, [400]))),
        takeUntil(action$.pipe(ofType(REPORT_TO_TEMPLATE_ASYNC.CANCEL)))
      )
    )
  )

const createCampaignFromIncidentsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_CAMPAIGN_FROM_INCIDENTS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .createNewCampaignFromScenario(
          action.payload.scenarioId,
          action.payload.localeId,
          state$.value.partnerOptions.selectedCompany?.value ?? action.payload.companyId,
          !!state$.value.app.data.partner
        )
        .pipe(
          map(({ response }) => createCampaignFromIncidentsSuccess(response)),
          catchError((error) =>
            merge(
              of(epicsErrorHandler(createCampaignFromIncidentsError, error, [400])),
              of(
                addToast({
                  message: extractErrorMessage(error),
                  type: 'error',
                  timeToDisappear: 8000,
                })
              )
            )
          ),
          takeUntil(action$.pipe(ofType(CREATE_CAMPAIGN_FROM_INCIDENTS_ASYNC.CANCEL)))
        )
    )
  )

const createCampaignFromScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .createNewCampaignFromScenario(
          action.payload.id,
          action.payload.localeId,
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          map(({ response }) => createCampaignFromScenarioSuccess(response)),
          catchError((error) =>
            of(epicsErrorHandler(createCampaignFromIncidentsError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpicsIsolated(
  fetchScenariosEpic,
  getScenarioEpic,
  addScenarioEpic,
  deleteScenarioEpic,
  saveScenarioEpic,
  getAwarenessDetailsEpic,
  reportToTemplateEpic,
  createCampaignFromIncidentsEpic,
  createCampaignFromScenarioEpic
)
